import { useEffect, useState } from 'react';
import './BottomNavigationBar.css';

export interface NavigationBarConfig {
  showBackButton?: boolean;
  showForwardButton?: boolean;
  showHomeButton?: boolean;
  showRefreshButton?: boolean;
  showUrlBar?: boolean;
  homeRoute?: string;
  onHome?: () => void;
  position?: 'fixed' | 'sticky' | 'relative';
  theme?: 'light' | 'dark' | 'auto';
}

export interface BottomNavigationBarProps extends NavigationBarConfig {}

export function BottomNavigationBar({
  showBackButton = true,
  showForwardButton = true,
  showHomeButton = true,
  showRefreshButton = true,
  showUrlBar = true,
  homeRoute = '/home',
  onHome,
  position = 'fixed',
  theme = 'auto',
}: BottomNavigationBarProps) {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  useEffect(() => {
    // Update path on navigation
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      setCanGoBack(window.history.length > 1);
      // Note: can't reliably detect forward navigation in browser history
      setCanGoForward(false);
    };

    // Listen to popstate for back/forward navigation
    window.addEventListener('popstate', handleLocationChange);

    // Initial state
    handleLocationChange();

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const handleBack = () => {
    if (canGoBack) {
      window.history.back();
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      window.history.forward();
    }
  };

  const handleHome = () => {
    if (onHome) {
      onHome();
      return;
    }
    window.location.href = homeRoute;
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const getDisplayPath = () => {
    // Convert path to readable format
    const path = currentPath === '/' ? '/home' : currentPath;
    return path.replace(/^\//, '').replace(/\//g, ' › ') || 'Home';
  };

  return (
    <nav
      className={`bottom-nav-bar bottom-nav-bar--${position} bottom-nav-bar--${theme}`}
      role="navigation"
      aria-label="Bottom navigation"
    >
      <div className="bottom-nav-bar__container">
        {/* Navigation buttons */}
        <div className="bottom-nav-bar__controls">
          {showBackButton && (
            <button
              className="bottom-nav-bar__button"
              onClick={handleBack}
              disabled={!canGoBack}
              aria-label="Go back"
              title="Back"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {showForwardButton && (
            <button
              className="bottom-nav-bar__button"
              onClick={handleForward}
              disabled={!canGoForward}
              aria-label="Go forward"
              title="Forward"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M8 4L14 10L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {showRefreshButton && (
            <button
              className="bottom-nav-bar__button"
              onClick={handleRefresh}
              aria-label="Refresh page"
              title="Refresh"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17 10C17 13.866 13.866 17 10 17C6.134 17 3 13.866 3 10C3 6.134 6.134 3 10 3C11.846 3 13.527 3.697 14.814 4.843M17 3V7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {showHomeButton && (
            <button
              className="bottom-nav-bar__button"
              onClick={handleHome}
              aria-label="Go to home"
              title="Home"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 10L10 3L17 10M4 9V17H8V13H12V17H16V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* URL/Path display */}
        {showUrlBar && (
          <div className="bottom-nav-bar__url-bar">
            <span className="bottom-nav-bar__path" title={currentPath}>
              {getDisplayPath()}
            </span>
          </div>
        )}
      </div>
    </nav>
  );
}

// Hook for easier integration in any page
export function useBottomNavigationBar(config?: NavigationBarConfig) {
  useEffect(() => {
    // Store config in global state if needed
    const navConfig = {
      enabled: true,
      ...config,
    };

    // Dispatch custom event to notify any listeners
    window.dispatchEvent(new CustomEvent('navigation-bar-config', {
      detail: navConfig
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('navigation-bar-config', {
        detail: { enabled: false }
      }));
    };
  }, [config]);
}
